import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../services/supabase/client'
import { User } from '@supabase/supabase-js'
import { authService } from '../services/authService'
import { Platform } from '../utils/platform'
import { profileService } from '../services/profileService'

export interface AuthState {
  user: User | null
  session: any | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Original auth methods
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
  
  // New methods using authService
  signupWithEmail: (email: string, password: string, metadata?: { [key: string]: any }) => 
    Promise<{ success: boolean; user?: User | null; error?: string }>
  signInWithMagicLink: (email: string, redirectTo?: string) => 
    Promise<{ success: boolean; error?: string }>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // Login with email and password
      async login(email: string, password: string) {
        try {
          // Clear any previous errors before attempting login
          set({ isLoading: true, error: null })
          
          console.log(`Auth store: Attempting to login with email: ${email}`)
          
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
          })
          
          if (error) {
            console.error('Auth store: Login error from Supabase:', error.message)
            throw error
          }
          
          if (!data || !data.user) {
            console.error('Auth store: No user returned from login')
            throw new Error('No user data returned')
          }
          
          console.log(`Auth store: Login successful for user ID: ${data.user.id}`)
          
          // Ensure user profile exists on login
          try {
            const displayName = data.user.user_metadata?.full_name || email.split('@')[0];
            await profileService.ensureUserProfile(data.user.id, email, displayName);
          } catch (profileError) {
            console.error('Error ensuring profile on login:', profileError);
            // Continue even if profile creation fails
          }
          
          set({ 
            user: data.user,
            session: data.session,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          console.error('Auth store: Login error:', error)
          
          // Set error state but DON'T throw the error
          // This allows the UI to handle the error without breaking the flow
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            error: error instanceof Error ? error.message : 'Failed to login',
            isLoading: false
          })
          
          // Now throw the error so the UI can display it
          throw error
        }
      },
      
      // Clear errors to reset state
      clearError() {
        set({ error: null })
      },
      
      // Register with email and password
      async register(email: string, password: string, displayName: string) {
        try {
          set({ isLoading: true, error: null })
          
          console.log(`Auth store: Attempting to register with email: ${email}`)
          
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: displayName
              }
            }
          })
          
          if (error) {
            console.error('Auth store: Registration error from Supabase:', error.message)
            throw error
          }
          
          if (!data || !data.user) {
            console.error('Auth store: No user returned from registration')
            throw new Error('No user data returned')
          }
          
          console.log(`Auth store: Registration successful for user ID: ${data.user.id}`)
          
          // Create user profile immediately instead of waiting for trigger
          // This ensures profile exists even if database triggers fail
          try {
            await profileService.ensureUserProfile(data.user.id, email, displayName);
            
            // Create a default cash account
            await profileService.createDefaultAccount(data.user.id);
          } catch (profileError) {
            console.error('Error creating profile during registration:', profileError);
            // Continue even if profile creation fails
          }
          
          set({ 
            user: data.user,
            session: data.session,
            isAuthenticated: !!data.user,
            isLoading: false,
            error: null
          })
        } catch (error) {
          console.error('Auth store: Registration error:', error)
          
          // Set error state
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            error: error instanceof Error ? error.message : 'Failed to register',
            isLoading: false
          })
          
          throw error
        }
      },
      
      // Logout
      async logout() {
        try {
          set({ isLoading: true, error: null })
          
          console.log('Auth store: Attempting to logout')
          
          const { error } = await supabase.auth.signOut()
          
          if (error) {
            console.error('Auth store: Logout error from Supabase:', error.message)
            throw error
          }
          
          console.log('Auth store: Logout successful')
          
          // Always reset state to logged out, even if there was an error
          // This ensures the user can get back to the login screen
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          })
        } catch (error) {
          console.error('Auth store: Logout error:', error)
          
          // Even on error, we still want to log the user out of the app
          // This prevents the user from being stuck in a logged-in state
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to logout'
          })
          
          // Don't throw the error for logout - we've already reset the state
          // and we don't want to prevent navigation back to login screen
        }
      },
      
      // Sign up with email (uses authService) - alternative to register
      async signupWithEmail(email, password, metadata) {
        try {
          set({ isLoading: true, error: null })
          
          console.log(`Auth store: Attempting to sign up with email using authService: ${email}`)
          
          const result = await authService.signupWithEmail(email, password, metadata)
          
          if (!result.success || result.error) {
            console.error('Auth store: Sign up failed:', result.error)
            set({
              isLoading: false,
              error: result.error || 'Failed to sign up'
            })
            return result
          }
          
          console.log('Auth store: Sign up successful')
          
          // Note: We don't set auth state here because the session will be handled
          // through the supabase auth state listener
          set({ isLoading: false, error: null })
          
          return result
        } catch (error) {
          console.error('Auth store: Sign up error:', error)
          
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to sign up'
          })
          
          return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Failed to sign up' 
          }
        }
      },
      
      // Sign in with magic link (uses authService)
      async signInWithMagicLink(email, redirectTo) {
        try {
          set({ isLoading: true, error: null })
          
          console.log(`Auth store: Sending magic link to: ${email}`)
          
          const result = await authService.signInWithMagicLink(email, redirectTo)
          
          if (!result.success || result.error) {
            console.error('Auth store: Magic link failed:', result.error)
            set({
              isLoading: false,
              error: result.error || 'Failed to send magic link'
            })
            return result
          }
          
          console.log('Auth store: Magic link sent successfully')
          
          // Note: We don't set auth state here because the user isn't logged in yet
          // They need to click the magic link, then the session will be handled
          // through the supabase auth state listener
          set({ isLoading: false, error: null })
          
          return result
        } catch (error) {
          console.error('Auth store: Magic link error:', error)
          
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to send magic link'
          })
          
          return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Failed to send magic link' 
          }
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated 
      })
    }
  )
) 